import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { MapPin, Home } from 'lucide-react';

interface StateNavigationProps {
  currentState: string;
  currentCountry: string;
  userState: string | null;
  isHomeLocation: boolean;
  onStateChange: (state: string, country: string) => void;
  onResetToHome: () => void;
}

// Country regions mapping
const COUNTRY_REGIONS: Record<string, string[]> = {
  'United States': [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
    'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
    'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
    'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
    'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
    'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
    'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
  ],
  'Canada': [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
    'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
    'Quebec', 'Saskatchewan', 'Yukon'
  ],
  'United Kingdom': [
    'England', 'Scotland', 'Wales', 'Northern Ireland'
  ],
  'Mexico': [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas', 'Chihuahua',
    'Coahuila', 'Colima', 'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'Mexico City',
    'México', 'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro',
    'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
    'Veracruz', 'Yucatán', 'Zacatecas'
  ],
  'Australia': [
    'New South Wales', 'Victoria', 'Queensland', 'Western Australia', 'South Australia',
    'Tasmania', 'Australian Capital Territory', 'Northern Territory'
  ],
  'Germany': [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg', 'Hesse',
    'Lower Saxony', 'Mecklenburg-Vorpommern', 'North Rhine-Westphalia', 'Rhineland-Palatinate',
    'Saarland', 'Saxony', 'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
  ],
  'France': [
    'Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Brittany', 'Centre-Val de Loire', 'Corsica',
    'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandy', 'Nouvelle-Aquitaine', 'Occitanie',
    'Pays de la Loire', 'Provence-Alpes-Côte d\'Azur', 'French Guiana', 'Guadeloupe', 'Martinique',
    'Mayotte', 'Réunion'
  ],
  'Spain': [
    'Andalusia', 'Aragon', 'Asturias', 'Balearic Islands', 'Basque Country', 'Canary Islands',
    'Cantabria', 'Castile and León', 'Castile-La Mancha', 'Catalonia', 'Extremadura', 'Galicia',
    'La Rioja', 'Madrid', 'Murcia', 'Navarre', 'Valencian Community', 'Ceuta', 'Melilla'
  ],
  'Brazil': [
    'Acre', 'Alagoas', 'Amapá', 'Amazonas', 'Bahia', 'Ceará', 'Distrito Federal', 'Espírito Santo',
    'Goiás', 'Maranhão', 'Mato Grosso', 'Mato Grosso do Sul', 'Minas Gerais', 'Pará', 'Paraíba',
    'Paraná', 'Pernambuco', 'Piauí', 'Rio de Janeiro', 'Rio Grande do Norte', 'Rio Grande do Sul',
    'Rondônia', 'Roraima', 'Santa Catarina', 'São Paulo', 'Sergipe', 'Tocantins'
  ],
  'Japan': [
    'Hokkaido', 'Aomori', 'Iwate', 'Miyagi', 'Akita', 'Yamagata', 'Fukushima', 'Ibaraki', 'Tochigi',
    'Gunma', 'Saitama', 'Chiba', 'Tokyo', 'Kanagawa', 'Niigata', 'Toyama', 'Ishikawa', 'Fukui',
    'Yamanashi', 'Nagano', 'Gifu', 'Shizuoka', 'Aichi', 'Mie', 'Shiga', 'Kyoto', 'Osaka', 'Hyogo',
    'Nara', 'Wakayama', 'Tottori', 'Shimane', 'Okayama', 'Hiroshima', 'Yamaguchi', 'Tokushima',
    'Kagawa', 'Ehime', 'Kochi', 'Fukuoka', 'Saga', 'Nagasaki', 'Kumamoto', 'Oita', 'Miyazaki',
    'Kagoshima', 'Okinawa'
  ],
  'Russia': [
    'Moscow', 'Saint Petersburg', 'Moscow Oblast', 'Leningrad Oblast', 'Krasnodar Krai',
    'Sverdlovsk Oblast', 'Rostov Oblast', 'Tatarstan', 'Bashkortostan', 'Chelyabinsk Oblast',
    'Samara Oblast', 'Nizhny Novgorod Oblast', 'Novosibirsk Oblast', 'Perm Krai', 'Voronezh Oblast',
    'Volgograd Oblast', 'Saratov Oblast', 'Republic of Dagestan', 'Stavropol Krai', 'Krasnoyarsk Krai',
    'Irkutsk Oblast', 'Primorsky Krai', 'Omsk Oblast', 'Orenburg Oblast', 'Tula Oblast'
  ],
  'China': [
    'Beijing', 'Shanghai', 'Tianjin', 'Chongqing', 'Anhui', 'Fujian', 'Gansu', 'Guangdong', 'Guizhou',
    'Hainan', 'Hebei', 'Heilongjiang', 'Henan', 'Hubei', 'Hunan', 'Jiangsu', 'Jiangxi', 'Jilin',
    'Liaoning', 'Qinghai', 'Shaanxi', 'Shandong', 'Shanxi', 'Sichuan', 'Yunnan', 'Zhejiang',
    'Guangxi', 'Inner Mongolia', 'Ningxia', 'Tibet', 'Xinjiang', 'Hong Kong', 'Macau', 'Taiwan'
  ]
};

// Country flags mapping
const COUNTRY_FLAGS: Record<string, string> = {
  'United States': '🇺🇸',
  'Canada': '🇨🇦',
  'United Kingdom': '🇬🇧',
  'Mexico': '🇲🇽',
  'Australia': '🇦🇺',
  'Germany': '🇩🇪',
  'France': '🇫🇷',
  'Spain': '🇪🇸',
  'Brazil': '🇧🇷',
  'Japan': '🇯🇵',
  'Russia': '🇷🇺',
  'China': '🇨🇳'
};

// Default regions for each country
const DEFAULT_REGIONS: Record<string, string> = {
  'United States': 'California',
  'Canada': 'Ontario',
  'United Kingdom': 'England',
  'Mexico': 'Mexico City',
  'Australia': 'New South Wales',
  'Germany': 'Bavaria',
  'France': 'Île-de-France',
  'Spain': 'Madrid',
  'Brazil': 'São Paulo',
  'Japan': 'Tokyo',
  'Russia': 'Moscow',
  'China': 'Beijing'
};

const COUNTRIES = Object.keys(COUNTRY_REGIONS);

const StateNavigation: React.FC<StateNavigationProps> = ({
  currentState,
  currentCountry,
  userState,
  isHomeLocation,
  onStateChange,
  onResetToHome,
}) => {
  const [country, setCountry] = useState(currentCountry);
  const regions = COUNTRY_REGIONS[country] || COUNTRY_REGIONS['United States'];

  useEffect(() => {
    setCountry(currentCountry);
  }, [currentCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const defaultRegion = DEFAULT_REGIONS[newCountry] || regions[0];
    onStateChange(defaultRegion, newCountry);
  };

  const handleStateChange = (newState: string) => {
    onStateChange(newState, country);
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-card rounded-lg border border-border">
      <div className="flex items-center gap-2 text-muted-foreground">
        <MapPin className="h-5 w-5" />
        <span className="text-sm font-medium">Viewing:</span>
      </div>
      
      <div className="flex flex-wrap items-center gap-3">
        <Select value={country} onValueChange={handleCountryChange}>
          <SelectTrigger className="w-[180px] bg-background">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {COUNTRIES.map(c => (
              <SelectItem key={c} value={c}>
                {COUNTRY_FLAGS[c]} {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={currentState} onValueChange={handleStateChange}>
          <SelectTrigger className="w-[200px] bg-background">
            <SelectValue placeholder="Select region" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {regions.map(region => (
              <SelectItem key={region} value={region}>
                {region}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {userState && !isHomeLocation && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onResetToHome}
            className="text-primary hover:text-primary/80"
          >
            <Home className="h-4 w-4 mr-1" />
            Back to {userState}
          </Button>
        )}
      </div>
    </div>
  );
};

export default StateNavigation;
